import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LifeBuoy, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useMeta, eventJsonLd } from "@/lib/seo";

const CATS = ["event", "report", "account", "dj", "technical", "other"];

export default function Help() {
  const { t, user } = useApp();
  useMeta({ title: t("helpSupport"), description: t("seoHelpDesc"), path: "/help", noindex: true });
  const [params] = useSearchParams();
  const [f, setF] = useState({ category: params.get("category") || "event", subject: "", description: "", event_link: params.get("event") || "", email: user?.email || "", attachment_url: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/flyer", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("attachment_url", data.url); toast.success(t("fileAttached"));
    } catch (err) { toast.error(err.response?.data?.detail || t("uploadFailed")); }
    setUploading(false);
  };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/support", f); setDone(data.ticket_id); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-10" data-testid="help-page">
      <div className="eyebrow text-primary">Soul Radar</div>
      <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tighter mt-2 flex items-center gap-3"><LifeBuoy className="w-8 h-8 text-primary" />{t("helpSupport")}</h1>
      <div className="mt-6 p-4 border border-foreground flex flex-wrap items-center justify-between gap-3" data-testid="help-faq-box">
        <span className="text-base">{t("helpFaqLead")}</span>
        <Link to="/faq" className="eyebrow text-primary hover:underline" data-testid="help-faq-link">{t("viewFaq")} →</Link>
      </div>
      {done ? (
        <div className="mt-8 border-t border-foreground pt-6" data-testid="help-done">
          <h2 className="font-display uppercase text-2xl">{t("helpThanks")}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t("helpTicket", { id: done })}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 border-t border-foreground pt-6 space-y-4" data-testid="help-form">
          <div><label className="eyebrow block mb-1.5">{t("category")} <span className="text-primary">*</span></label>
            <select className="gg-input" value={f.category} onChange={e => set("category", e.target.value)} data-testid="help-category">{CATS.map(c => <option key={c} value={c}>{t(`helpCat.${c}`)}</option>)}</select></div>
          <div><label className="eyebrow block mb-1.5">{t("subject")} <span className="text-primary">*</span></label><input required maxLength={120} className="gg-input" value={f.subject} onChange={e => set("subject", e.target.value)} data-testid="help-subject" /></div>
          <div><label className="eyebrow block mb-1.5">{t("description")} <span className="text-primary">*</span></label><textarea required rows={6} className="gg-input" value={f.description} onChange={e => set("description", e.target.value)} data-testid="help-description" /></div>
          <div><label className="eyebrow block mb-1.5">{t("eventLinkOptional")}</label><input className="gg-input" placeholder="https://" value={f.event_link} onChange={e => set("event_link", e.target.value)} data-testid="help-event-link" /></div>
          <div><label className="eyebrow block mb-1.5">{t("email")} <span className="text-primary">*</span></label><input required type="email" className="gg-input" value={f.email} onChange={e => set("email", e.target.value)} data-testid="help-email" /></div>
          <div><label className="eyebrow block mb-1.5">{t("screenshotOptional")}</label>
            <input type="file" accept="image/*,.pdf" onChange={e => upload(e.target.files?.[0])} className="block text-sm" data-testid="help-file" />
            {f.attachment_url && <a href={f.attachment_url} target="_blank" rel="noreferrer" className="text-xs underline mt-1 inline-block" data-testid="help-attachment">{t("fileAttached")} ↗</a>}</div>
          <button type="submit" disabled={busy || uploading} className="gg-btn-primary" data-testid="help-submit"><Send className="w-4 h-4" />{t("sendRequest")}</button>
        </form>
      )}
    </div>
  );
}
